import { showErrorToast, showSuccessToast } from '@bpinternal/ui-kit-next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser } from '~/hooks'
import { getQueryKey } from '~/services'
import { HttpError } from '~/shared'
import { mapErrorCodeToMessage } from '../helpers'
import { BillingApiClient } from '../services'
import { AddonDetails, AddonQuotaType, ProductQuantityMap } from '../types/index'
import { useActiveSubscription } from './useActiveSubscription'
import { usePlanProducts } from './usePlanProducts'

export function usePurchaseAddons(workspaceId: string, onSuccess?: () => void) {
  const user = useCurrentUser()
  const { monthly: monthlyActiveSubcription } = useActiveSubscription(workspaceId)
  const queryClient = useQueryClient()
  const { nextPhasePlan } = usePlanProducts(workspaceId)

  return useMutation({
    gcTime: 0,
    mutationFn: async ({
      addonDeltas,
      addons,
    }: {
      addonDeltas: Record<AddonQuotaType, number>
      addons: AddonDetails[]
    }) => {
      if (monthlyActiveSubcription) {
        const payload = addons.map((addon) => ({
          price: addon.priceId,
          quantity: addon.nextPaymentQuantity + addonDeltas[addon.quotaName],
        }))

        if (nextPhasePlan.cycle === 'monthly' && nextPhasePlan.legacyName !== 'community') {
          payload.push({ price: nextPhasePlan.priceId, quantity: 1 })
        }

        await BillingApiClient.updateSubscription(workspaceId, {
          productQuantityConfig: [...payload],
          subscriptionId: monthlyActiveSubcription.id,
        })
      } else {
        const productQuantityMap = addonDetailsToProductQuantityMap(addons, addonDeltas)
        const { url } = await BillingApiClient.generateCheckoutSession(workspaceId, productQuantityMap, user.email)
        window.location.href = url
        return
      }
    },
    onSuccess: async () => {
      showSuccessToast('Your subscription has been successfully updated')

      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/billing/subscriptions', { workspaceId }),
      })
      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/billing/subscriptionSchedule', { workspaceId }),
      })

      onSuccess?.()
    },
    onError: (error) => {
      const httpError = error as HttpError
      showErrorToast(
        mapErrorCodeToMessage(httpError.data?.code) ??
          error.message ??
          'There was an error while updating your subscription. Please try again.'
      )
    },
    meta: {
      suppressError: true,
    },
  })
}

const addonDetailsToProductQuantityMap = (
  addonDetails: AddonDetails[],
  addonDeltas: Record<AddonQuotaType, number>
): ProductQuantityMap => {
  return addonDetails.reduce((acc: ProductQuantityMap, addon) => {
    acc[addon.productId] = {
      quantity: addon.nextPaymentQuantity + addonDeltas[addon.quotaName],
      priceId: addon.priceId,
    }
    return acc
  }, {})
}
